import { BaseModel } from "./BaseModel";
import { IAutoplaySettings } from "../server/data/IAutoplayData";
import BetModel from "./BetModel";
import GameModel from "./GameModel";
import Utils from '../utils/Utils';
import SpinModel from "./SpinModel";
import { LossLimitComputeType } from "./../LossLimitComputeType";


class AutoplayModel extends BaseModel<IAutoplaySettings> {
    public lossLimits: number[] = [];
    public singleWinLimits: number[] = [];
    public totalWinLimits: number[] = [];
    public spinPresets: number[] = [];
    public defaultSpinPreset: number;
    public autoSpinNum: number = 0;
    public lossLimit: number = 0;
    public winLimit: number = 0;
    public totalWinLimit: number = 0;
    public autospinStartBalance: number;
    public regulationSettings:{[key: string]: AutoplaySettings} = {
        'unregulated': {
            lossLimitRequired: false
        },
        'uk': {
            lossLimitRequired: true
        }
    };
    public stopOnAnyWin: boolean = false;
    public stopOnBigWin: boolean = false;
    public stopOnFeatureWin: boolean = false;
    public autoSpinLimitReached: 'total_win' | 'win' | 'loss' | 'any_win' | 'feature' | 'big_win' | null;
    public initialAutospinNum: number = 0;
    public initialWinLimit: number = 0;
    public initialTotalWinLimit: number = 0;
    public initialLossLimit: number = 0;
    public initialStopOnAnyWin: boolean = false;

    protected _autospinStarted: boolean;

    // Used to store the current win/loss, since the autoplay started.
    protected totalProfit: number;

    protected lossLimitComputeType = LossLimitComputeType.NonBalanceBased;

    constructor() {
        super();
        this.autoSpinNum = 0;
        this.defaultSpinPreset = 0;
        // this.tmpLossLimit = LocalData.getNumber(LocalStorageVars.LOSS_LIMIT);
        // this.tmpWinLimit = LocalData.getNumber(LocalStorageVars.WIN_LIMIT);

    }

    public setAutoSpin(autoSpinNum: number)
    {
        this.autospinStartBalance = BetModel.balance;
        this._autospinStarted = false;
        if (autoSpinNum > 0) {
            this.initialAutospinNum = autoSpinNum;
        }

        this.autoSpinNum = autoSpinNum;
    }

    public startAutoSpin() {
        this._autospinStarted = true;
        this.totalProfit = 0;
    }

    public decrementAutoSpinNum()
    {
        if (this.autoSpinNum > 0 )
        {
            this.totalProfit += SpinModel.winAmount;
            if (!SpinModel.isInFreespins() && !SpinModel.isInRespins()){
                this.totalProfit -= BetModel.totalBet;
                this.autoSpinNum--;

            }
        }
        this._autospinStarted = this.autoSpinNum > 0;
    }

    public getAutoSpinNum()
    {
        return this.autoSpinNum;
    }

    public isAutoSpinActive()
    {
        return this.autoSpinNum > 0 && this._autospinStarted;
    }

    public hasAutospinStarted()
    {
        return this._autospinStarted;
    }

    /**
     * Get loss limit for autospin used for UK market
     * @returns {Number}
     */
    public getLossLimit(): number
    {
        return this.lossLimit
    }

    public checkLossLimitReached(): boolean
    {
        if (SpinModel.isInFreespins() || SpinModel.hasFreespinSelectScreen()) {
            return false;
        }

        const loss = this.getAutospinLoss();
        const limit = this.getLossLimit();
        let result = (Utils.almostEquals(loss, -limit) || loss < -limit) &&
            (this.getRegulationSettings().lossLimitRequired || limit > 0);

        if(result) {
            this.autoSpinLimitReached = 'loss';
        }
        return result;
    }

    /**
     * Set loss limit for autospin used for UK market
     * @param limit {Number}
     */
    public setLossLimit(limit: number)
    {
        this.lossLimit = limit;
    }

    /**
     * Get win limit for autospin used for UK market
     * @returns {Number}
     */
    public getWinLimit(): number
    {
        return this.winLimit;
    }

    /**
     * Set win limit for autospin used for UK market
     * @returns {Number}
     */
    public setWinLimit(limit: number)
    {
        this.winLimit = limit;
    }

    public checkWinLimitReached(win: number): boolean
    {
        const winLimit = this.getWinLimit();
        let result = winLimit > 0 && (Utils.almostEquals(win, winLimit) || win > winLimit); 
        if (result) {
            this.autoSpinLimitReached = 'win';
        }

        return result;
    }

    public getTotalWinLimit(): number {
        return this.totalWinLimit;
    }

    public setTotalWinLimit(limit: number) {
        this.totalWinLimit = limit;
    }

    public checkTotalWinLimitReached(): boolean
    {
        const wins = this.getCumulativeWins();
        let result = this.totalWinLimit > 0 && (Utils.almostEquals(wins, this.totalWinLimit) || wins > this.totalWinLimit);
        if (result) {
            this.autoSpinLimitReached = 'total_win';
        }

        return result;
    }

    /**
     * Losses since autospin started
     * @returns {Number}
     */
    public getAutospinLoss(): number
    {
        const win = SpinModel.winAmount;
        switch (this.lossLimitComputeType) {
            case LossLimitComputeType.BalanceBased:
                return BetModel.balance + win - this.autospinStartBalance;
            case LossLimitComputeType.NonBalanceBased:
                return this.totalProfit + win;
            default:
                throw new Error("Not implemented LossLimitComputeType: " + this.lossLimitComputeType);
        }
    }

    /**
     * For total win. Accumulates all wins in current autoplay.
     * @returns {Number}
     */
    public getCumulativeWins() {
        switch (this.lossLimitComputeType) {
            case LossLimitComputeType.NonBalanceBased:
                return this.totalProfit;
            case LossLimitComputeType.BalanceBased:
                const loss = this.getAutospinLoss();
                const result = loss + BetModel.totalBet * (this.initialAutospinNum - this.autoSpinNum);
                return result;
            default:
                throw new Error("Not implemented LossLimitComputeType: " + this.lossLimitComputeType);
        }
    }

    /**
     * The values through which autospin cycles. Different for UK market
     * @returns {Array}
     */
    public getAutospinPresets(): number[]
    {
        return this.spinPresets;
    }

    /**
     * Get the maximum autospin number
     * @returns {Integer}
     */
    public getMaxAutospins()
    {
        const presets = this.spinPresets;
        return presets[presets.length - 1];
    }


    /**
     * Is autospinning enabled (i.e. Can you use it)
     * @returns {Boolean}
     */
    public autospinIsEnabled(): boolean
    {
        return this.autoSpinNum > 0 && !this.getRegulationSettings().lossLimitRequired || this.getLossLimit() > 0;
    }

    public toggleStopOnAnyWin() {
        if(this.stopOnAnyWin) {
            this.stopOnAnyWin = false;
        }
        else {
            this.stopOnAnyWin = true;
        }
    }
    public toggleStopOnBigWin() {
        if(this.stopOnBigWin) {
            this.stopOnBigWin = false;
        }
        else {
            this.stopOnBigWin = true;
        }
    }


    public checkStopOnBigWin(win: number) {
        let result = this.stopOnBigWin && win > GameModel.gameConfig.bigWinRatioLevels[0]*BetModel.totalBet;
        if(result) {
            this.autoSpinLimitReached = 'big_win';
        }
        return result;
    }

    public checkStopOnAnyWin(win: number) {
        let result = this.stopOnAnyWin && win > 0;
        if(result) {
            this.autoSpinLimitReached = 'any_win';
        }
        return result;
    }

    public checkFeatureWin(hasFeature: boolean) {
        let result = this.stopOnFeatureWin && hasFeature;
        if(result) {
            this.autoSpinLimitReached = 'feature';
        }
        return result;
    }

    public toggleStopOnFeatureWin() {
        if(this.stopOnFeatureWin) {
            this.stopOnFeatureWin = false;
        }
        else {
            this.stopOnFeatureWin = true;
        }
    }

    public parseConfig = (data: any) => {
        Utils.serialize(data, this);
      }

    public getRegulationSettings(): AutoplaySettings {
        if(this.regulationSettings[GameModel.regulation]) {
            return this.regulationSettings[GameModel.regulation]
        }
        return this.regulationSettings['unregulated'];
    }

    public clearLimits() {
        this.lossLimit = 0;
        this.totalWinLimit = 0;
        this.winLimit = 0;
        this.stopOnAnyWin = false;
        this.stopOnBigWin = false;
        this.stopOnFeatureWin = false;
    }
}

export default new AutoplayModel();

interface AutoplaySettings {
    lossLimitRequired: boolean;
    timeBetweenSpins?: number;
}
